import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  createSchoolNetwork,
  getAllAvailableSchools,
  updateNetworkSchools,
} from '@/api/school-networks';
import type { AvailableSchool, SchoolNetworkType } from '@/types/school-network';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { MultiSelect } from '@/components/ui/multi-select';
import type { MultiSelectOption } from '@/components/ui/multi-select';
import { ArrowLeft, Network, Save, Loader2, School, Server, Globe, Mail } from 'lucide-react';

function slugify(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function SchoolNetworkFormPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [type, setType] = useState<SchoolNetworkType>(
    searchParams.get('type') === 'physical' ? 'physical' : 'virtual'
  );
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [host, setHost] = useState('');
  const [hostTouched, setHostTouched] = useState(false);
  const [senderEmail, setSenderEmail] = useState('');
  const [senderName, setSenderName] = useState('');
  const [availableSchools, setAvailableSchools] = useState<AvailableSchool[]>([]);
  const [selectedSchoolIds, setSelectedSchoolIds] = useState<number[]>([]);
  const [schoolsLoading, setSchoolsLoading] = useState(false);

  // Host suggéré à partir du nom tant qu'il n'a pas été édité à la main
  useEffect(() => {
    if (type === 'physical' && !hostTouched) {
      const slug = slugify(name);
      setHost(slug ? `${slug}.ecole-futee.com` : '');
    }
  }, [name, type, hostTouched]);

  const schoolOptions: MultiSelectOption[] = useMemo(
    () =>
      availableSchools.map((school) => {
        const memberships = school.virtualNetworks?.map((n) => n.name) ?? [];
        return {
          value: String(school.id),
          label: school.name,
          description: memberships.length
            ? `(${school.code}) — déjà dans : ${memberships.join(', ')}`
            : `(${school.code})`,
        };
      }),
    [availableSchools]
  );

  const selectedSchoolValues = useMemo(() => selectedSchoolIds.map(String), [selectedSchoolIds]);

  useEffect(() => {
    const fetchSchools = async () => {
      try {
        setSchoolsLoading(true);
        setAvailableSchools(await getAllAvailableSchools());
      } catch (err) {
        console.error(err);
      } finally {
        setSchoolsLoading(false);
      }
    };
    fetchSchools();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Le nom est requis');
      return;
    }
    if (type === 'physical' && !host.trim()) {
      setError('Le domaine (host) est requis pour un réseau physique');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const result = await createSchoolNetwork({
        type,
        name: name.trim(),
        description: description.trim() || undefined,
        host: type === 'physical' ? host.trim() : undefined,
        senderEmail: type === 'physical' ? senderEmail.trim() || undefined : undefined,
        senderName: type === 'physical' ? senderName.trim() || undefined : undefined,
      });

      // Réseau virtuel : association en masse possible dès la création.
      // Réseau physique : le rattachement (définitif) se fait depuis le
      // détail, école par école, avec confirmation.
      if (type === 'virtual' && selectedSchoolIds.length > 0) {
        await updateNetworkSchools(result.id, selectedSchoolIds);
      }

      navigate(`/school-networks/${result.id}`);
    } catch (err: any) {
      setError(
        err?.response?.data?.error ||
          (err instanceof Error ? err.message : 'Erreur lors de la création du réseau')
      );
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon-sm" asChild>
          <Link to="/school-networks">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Network className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
            Nouveau réseau
          </h1>
          <p className="text-sm text-muted-foreground">
            Créez un réseau physique (group tenant V2) ou virtuel (agrégation)
          </p>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">{error}</div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Type selection */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Type de réseau</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2">
              <button
                type="button"
                onClick={() => setType('virtual')}
                className={`rounded-lg border-2 p-4 text-left transition-colors ${
                  type === 'virtual'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-muted-foreground/40'
                }`}
              >
                <div className="flex items-center gap-2 font-medium">
                  <Network className="h-4 w-4" />
                  Réseau virtuel
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  Simple regroupement d'écoles pour l'agrégation des métriques. Aucune incidence
                  technique — une école peut appartenir à plusieurs réseaux virtuels.
                </p>
              </button>
              <button
                type="button"
                onClick={() => setType('physical')}
                className={`rounded-lg border-2 p-4 text-left transition-colors ${
                  type === 'physical'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-muted-foreground/40'
                }`}
              >
                <div className="flex items-center gap-2 font-medium">
                  <Server className="h-4 w-4" />
                  Réseau physique
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  Crée un group tenant V2 et sa marque blanche (domaine dédié). Le rattachement
                  d'une école est définitif et se fait avec confirmation.
                </p>
              </button>
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-6 md:grid-cols-2">
          {/* Network info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Informations</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">
                  Nom <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ex: Réseau Paris Est"
                  required
                />
              </div>

              {type === 'physical' && (
                <div className="space-y-2">
                  <Label htmlFor="host" className="flex items-center gap-1">
                    <Globe className="h-3.5 w-3.5" />
                    Domaine (host) <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="host"
                    value={host}
                    onChange={(e) => {
                      setHostTouched(true);
                      setHost(e.target.value);
                    }}
                    placeholder="mon-reseau.ecole-futee.com"
                    className="font-mono"
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    Domaine du group tenant V2 (marque blanche). Les utilisateurs du réseau se
                    connecteront via ce domaine.
                  </p>
                </div>
              )}

              {type === 'physical' && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="senderEmail" className="flex items-center gap-1">
                      <Mail className="h-3.5 w-3.5" />
                      Email d'expédition
                    </Label>
                    <Input
                      id="senderEmail"
                      type="email"
                      value={senderEmail}
                      onChange={(e) => setSenderEmail(e.target.value)}
                      placeholder="no-reply@mon-reseau.fr"
                    />
                    <p className="text-xs text-muted-foreground">
                      Adresse expéditrice des emails du réseau, poussée dans les réglages du group
                      tenant V2.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="senderName">Nom d'expéditeur</Label>
                    <Input
                      id="senderName"
                      value={senderName}
                      onChange={(e) => setSenderName(e.target.value)}
                      placeholder="Ex: Réseau Paris Est"
                    />
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Description optionnelle du réseau..."
                  rows={4}
                />
              </div>
            </CardContent>
          </Card>

          {/* Schools selection — réseaux virtuels uniquement */}
          {type === 'virtual' ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <School className="h-4 w-4" />
                  Écoles du réseau ({selectedSchoolIds.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {schoolsLoading ? (
                  <Skeleton className="h-12" />
                ) : (
                  <MultiSelect
                    options={schoolOptions}
                    selected={selectedSchoolValues}
                    onChange={(values) => setSelectedSchoolIds(values.map(Number))}
                    placeholder="Sélectionner des écoles..."
                    searchPlaceholder="Rechercher une école..."
                    emptyMessage="Aucune école trouvée"
                  />
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <School className="h-4 w-4" />
                  Écoles du réseau
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Les écoles se rattachent au réseau physique <strong>après sa création</strong>,
                  une par une, avec leur propre domaine et une confirmation explicite : le
                  rattachement est <strong>définitif</strong>.
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex items-center justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/school-networks')}
            disabled={saving}
          >
            Annuler
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {type === 'physical' ? 'Création du group tenant V2...' : 'Enregistrement...'}
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Créer le réseau
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
